import React, { useEffect, useState, useCallback } from "react";
import { color } from "d3-color";
import { interpolateRgb } from "d3-interpolate";
import LiquidFillGauge from "react-liquid-gauge";
import { PDFViewer } from "@react-pdf/renderer";
import axios from "axios";
import AmmoniaInventoryPDF from "./AmmoniaInventoryPDF";
import { useAppContext } from "../../context/AppContext.tsx";
import { formatNumberWithCommas } from "../utils/numberFormat";

const AmmoniaInventory = () => {
  let { ladId } = useAppContext();
  if(!ladId){
    ladId = localStorage.getItem("ladId")
  }
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [ammoniaData, setAmmoniaData] = useState(null);
  const [showPDF, setShowPDF] = useState(false);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [pipesData, setPipesData] = useState({});

  const calculateAmmoniaCapacities = useCallback((responseData) => {
    if (!Array.isArray(responseData)) return [];

    const toNumber = (val) => {
      if (val == null) return 0;
      const n = parseFloat(String(val).replace(/,/g, ""));
      return Number.isFinite(n) ? n : 0;
    };

    const equipments = [];
    const pipes = [];

    responseData.forEach((item) => {
      if (item?.engg_object) {
        equipments.push(item);
      } else if (item?.pipeId || item?.pipe || item?.segmentId) {
        pipes.push(item);
      }
    });

    const equipmentGrouped = {};
    equipments.forEach((item) => {
      const category = item?.engg_object?.name || "Unknown";
      if (!equipmentGrouped[category]) equipmentGrouped[category] = [];
      equipmentGrouped[category].push(item);
    });

    const result = [];

    Object.entries(equipmentGrouped).forEach(([category, items]) => {
      let totalCapacity = 0;
      let count = items.length;

      items.forEach((item) => {
        if (Array.isArray(item.dataform_json)) {
          item.dataform_json.forEach((block) => {
            if (Array.isArray(block.fields)) {
              block.fields.forEach((field) => {
                const name = String(field?.fieldname || "")
                  .trim()
                  .toLowerCase();
                if (name === "ammonia capacity") {
                  totalCapacity += toNumber(field?.value || 0);
                }
              });
            }
          });
        }
      });

      if (count > 0) {
        result.push({
          equipmentName: category,
          numberOfUnits: count,
          totalCapacity,
        });
      }
    });
    let totalPipeCapacity = 0;
    if (pipes.length > 0) {
      const pipeCount = pipes.length;

       totalPipeCapacity = pipes.reduce(
        (sum, item) => sum + toNumber(item?.ammoniaCapacity || 0),
        0
      );

      setPipesData({
        equipmentName: "Pipes",
        numberOfUnits: pipeCount,
        totalCapacity: totalPipeCapacity,
      });
    }

    const accuracy = 5; // ±5%
    const estimatedEquipmentCharge = result.reduce(
      (sum, eq) => sum + (eq?.totalCapacity || 0),
      0
    );
    const estimatedCharge =
      estimatedEquipmentCharge + totalPipeCapacity;
    const lowEstimation = estimatedCharge * (1 - accuracy / 100);
    const highEstimation = estimatedCharge * (1 + accuracy / 100);

    const customerName = JSON.parse(localStorage.getItem("customer"));
    const customer = customerName.name
    const plant = JSON.parse(localStorage.getItem("location"));
		const plantAddress = [plant?.address, plant?.city, plant?.state, plant?.country, plant?.zipcode]
			.filter(Boolean)
			.join(', ');

		const location = `${plant.plant_name || ''}\n${plantAddress || ''}`;
    const customerId = localStorage.getItem("customerId") || "000";
    const locationId = localStorage.getItem("selectedLocationId") || "000";    

		const documentId = `${customerId}-${locationId}-${ladId}`;

    return {
      chargeCapacities: result,
      estimatedEquipmentCharge,
      estimatedCharge,
      lowEstimation,
      highEstimation,
      accuracy,
      customer,
      location,
      documentId,
      dateOfService: new Date(),
    };
  }, []);

  useEffect(() => {
    if (!ladId || !baseUrl || isNaN(ladId) || Array.isArray(ladId)) return;

    const fetchAmmoniaInventory = async () => {
      setIsFetching(true);
      setError(null);
 
      try {
        const accessToken = sessionStorage.getItem("accessToken");
        const headers = accessToken ? { Authorization: `${accessToken}` } : {};
      
        const response = await axios.get(
          `${baseUrl}/ammoniaInventory/${ladId}`,
          { headers }
        );

        if (response?.data) {
          const capacities = calculateAmmoniaCapacities(response.data);
          setAmmoniaData(capacities);
        } else {
          setAmmoniaData(null);
        }
      } catch (err) {
        console.error("Error fetching ammonia inventory:", err);
        setError("Failed to load ammonia inventory.");
      } finally {
        setIsFetching(false);
      }
    };

    fetchAmmoniaInventory();
  }, [ladId, baseUrl, calculateAmmoniaCapacities]);

  const radius = 100;
  const chartValue = 100;
  const interpolate = interpolateRgb("#76daff", "#76daff");
  const fillColor = interpolate(50 / 100);
  const gradientStops = [
    {
      key: "0%",
      stopColor: color(fillColor).darker(0.5).toString(),
      stopOpacity: 1,
      offset: "0%",
    },
    { key: "50%", stopColor: fillColor, stopOpacity: 0.75, offset: "50%" },
    {
      key: "100%",
      stopColor: color(fillColor).brighter(0.5).toString(),
      stopOpacity: 0.5,
      offset: "100%",
    },
  ];

  if (isFetching)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  if (error) return <div className="text-danger text-center py-4">{error}</div>;
  if (!ammoniaData && !isFetching)
    return (
      <div className="text-center py-4">
        No ammonia inventory data available.
      </div>
    );

  const {
    estimatedCharge,
    accuracy,
    lowEstimation,
    highEstimation,
    dateOfService,
  } = ammoniaData;

  return (
    <div className="row">
      <div className="col-12 px-3 pt-4 pb-2 d-flex align-items-center justify-content-between">
        <div className="fnt14 fw-600">Ammonia Inventory</div>
        <button className="button-secondary" onClick={() => setShowPDF(true)}>
          View PDF
        </button>
      </div>

      <div className="col-12">
        <div id="chart" className="text-center my-3">
          <LiquidFillGauge
            style={{ margin: "0 auto" }}
            width={radius * 2}
            height={radius * 2}
            value={chartValue}
            percent=" lbs"
            textSize={1}
            textRenderer={(props) => {
              const radius = Math.min(props.height / 2, props.width / 2);
              const textPixels = (props.textSize * radius) / 2;
              const valueStyle = { fontSize: textPixels * 0.5 };
              const percentStyle = { fontSize: textPixels * 0.5 };
              return (
                <tspan>
                  <tspan className="chart-value-color" style={valueStyle}>
                    {formatNumberWithCommas(estimatedCharge)}
                  </tspan>
                  <tspan className="chart-value-color" style={percentStyle}>
                    {props.percent}
                  </tspan>
                </tspan>
              );
            }}
            riseAnimation
            waveAnimation
            waveFrequency={2}
            waveAmplitude={1}
            gradient
            gradientStops={gradientStops}
            circleStyle={{ fill: fillColor }}
            waveStyle={{ fill: fillColor }}
          />
          <div className="d-flex align-items-center justify-content-between px-3">
            <div className="fnt12 fw-500 py-2">
              Date:{" "}
              <span className="text-info fnt10 fw-500">
                {new Date(dateOfService).toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>

            <div className="fnt12 fw-500 py-2">
              Accuracy:{" "}
              <span className="text-info fnt10 fw-500">+/- {accuracy}%</span>
            </div>
          </div>
        </div>
      </div>

      {showPDF && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ammonia Inventory Report</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPDF(false)}
                ></button>
              </div>
              <div className="modal-body p-0" style={{ height: "80vh" }}>
                <PDFViewer width="100%" height="100%">
                  <AmmoniaInventoryPDF
                    data={ammoniaData}
                    pipesData={pipesData}
                  />
                </PDFViewer>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmmoniaInventory;
